/*
 * De routetabel is de enige plek waar staat welke pagina's er zijn (requirements §3).
 * Drie dingen lezen eruit: de router, de navigatie, en het prerender-script — dat
 * laatste is de reden dat titel en description hier staan en niet in de component.
 * Zonder die koppeling zou een nieuwe route stilzwijgend zonder HTML-titel opleveren.
 *
 * Titels en descriptions zijn letterlijk overgenomen uit legacy/current; waar de
 * bestaande site geen description heeft, staat hier ook geen verzonnen tekst maar
 * een korte samenvatting van de pagina zelf.
 *
 * WP-16 breidt dit uit met canonical, OG-tags en structured data. De vorm ervan
 * verandert dan niet, alleen het aantal velden.
 */

export type NavPlaats = 'hoofd' | 'footer' | 'geen'

export interface RouteMeta {
  /** Pad zoals react-router en het prerender-script het kennen. */
  readonly pad: string
  /** Label in de navigatie. */
  readonly label: string
  /** Wordt <title>. */
  readonly titel: string
  /** Wordt <meta name="description">. */
  readonly omschrijving: string
  readonly navPlaats: NavPlaats
  /**
   * Zet <meta name="robots" content="noindex">. De statuspagina hoort niet in
   * zoekresultaten: hij is alleen zinvol met een verwijzing uit de betaalflow.
   */
  readonly noindex?: boolean
  /**
   * Deze route levert bewust alleen een schil op: de inhoud hangt af van een
   * serveraanroep en is bij de build niet te renderen.
   *
   * De acceptatietest uit ADR-0011 slaat de inhoudscontrole dan over, maar eist
   * wel `noindex` — een lege pagina in de zoekresultaten is erger dan geen
   * pagina. Zet dit dus niet om een falende controle te omzeilen; het is een
   * verklaring, geen ontsnapping.
   */
  readonly alleenSchil?: boolean
  /**
   * Welke footer de pagina krijgt. De bestaande site zet een compacte regel onder
   * de privacy- en bedanktpagina en de volledige footer onder de rest.
   */
  readonly footer?: 'volledig' | 'compact'
}

export const routes: readonly RouteMeta[] = [
  {
    pad: '/',
    label: 'Home',
    titel:
      'GPKeuringen: gehandicaptenparkeerkaart aanvragen | Onafhankelijk sociaal medisch advies',
    omschrijving:
      'Vraag uw gehandicaptenparkeerkaart (GPK) aan. Onafhankelijk sociaal medisch advies door BIG-geregistreerde artsen. Vlot en voordelig beoordeeld, advies meestal dezelfde dag opgestuurd.',
    navPlaats: 'hoofd',
  },
  {
    pad: '/beoordeling',
    label: 'Beoordeling',
    titel: 'Het stappenplan: van aanvraag tot advies | GPKeuringen',
    omschrijving:
      'Zo werkt het bij GPKeuringen: van kaartkeuze en aanvraag tot betaling, afspraak, medische beoordeling en advies.',
    navPlaats: 'hoofd',
  },
  {
    pad: '/over-ons',
    label: 'Over ons',
    titel: 'Over ons: specialist in GPK-beoordelingen | GPKeuringen',
    omschrijving:
      'GPKeuringen richt zich volledig op medische beoordelingen voor gehandicaptenparkeerkaarten. Objectief, deskundig en cliëntgericht, met korte doorlooptijden.',
    navPlaats: 'hoofd',
  },
  {
    pad: '/contact',
    label: 'Contact',
    titel: 'Contact | GPKeuringen',
    omschrijving:
      'Neem contact op met GPKeuringen via info@gpkeuringen.nl of het contactformulier.',
    navPlaats: 'hoofd',
  },
  {
    pad: '/bedankt',
    label: 'Bedankt',
    titel: 'Uw aanvraag | GPKeuringen',
    omschrijving:
      'De status van uw aanvraag voor een gehandicaptenparkeerkaart bij GPKeuringen.',
    navPlaats: 'geen',
    noindex: true,
    footer: 'compact',
  },
  {
    // De echte route is /betaal/:token; wat hier geprerenderd wordt is de schil.
    // staticwebapp.config.json stuurt /betaal/* hierheen, waarna de pagina het
    // token uit de URL leest en de status ophaalt. Dat is de enige route die
    // niet zonder JavaScript werkt, en dat kan ook niet: het antwoord hangt af
    // van een aanvraag die alleen de server kent.
    pad: '/betaal',
    label: 'Betalen',
    titel: 'Uw betaling | GPKeuringen',
    omschrijving: 'Rond de betaling van uw aanvraag bij GPKeuringen af.',
    navPlaats: 'geen',
    noindex: true,
    alleenSchil: true,
    footer: 'compact',
  },
  {
    pad: '/privacy',
    label: 'Privacyverklaring',
    titel: 'Privacyverklaring | GPKeuringen',
    omschrijving:
      'Hoe GPKeuringen omgaat met uw persoonsgegevens: welke gegevens wij verwerken, waarom en hoe lang wij ze bewaren.',
    navPlaats: 'footer',
    footer: 'compact',
  },
  {
    pad: '/voorwaarden',
    label: 'Algemene voorwaarden',
    titel: 'Algemene voorwaarden | GPKeuringen',
    omschrijving:
      'De algemene voorwaarden van GPKeuringen voor de sociaal-medische beoordeling voor de gehandicaptenparkeerkaart.',
    navPlaats: 'footer',
    footer: 'compact',
  },
]

export const hoofdnavigatie = routes.filter((r) => r.navPlaats === 'hoofd')
export const footernavigatie = routes.filter((r) => r.navPlaats === 'footer')

export function routeVoorPad(pad: string): RouteMeta | undefined {
  return routes.find((r) => r.pad === pad)
}

/** Titel en description voor een pad dat niet in de tabel staat (404). */
export const nietGevondenMeta: RouteMeta = {
  pad: '*',
  label: 'Pagina niet gevonden',
  titel: 'Pagina niet gevonden | GPKeuringen',
  omschrijving: 'Deze pagina bestaat niet of is verplaatst.',
  navPlaats: 'geen',
  noindex: true,
  footer: 'compact',
}
